<template>
	<div>
		<LoadingIcon v-if="isLoading" alignment="center" />
		<div v-if="settings">
			<div
				v-for="(preference, index) in settings.preferences"
				class="alert-container"
				data-test="loan-setting"
				:class="{ disable: disableSetting(preference.type.desc) }"
				:key="index"
				@click="handleConfigureAlerts(preference)"
			>
				<div class="alert-type-container">
					<AlertTypeIcon :alertType="preference.type.desc" />
					<div class="alert-type" data-test="alert-type">
						<span
							role="button"
							:aria-label="preference.type.desc"
							class="type"
							:class="{
								disable: disableSetting(preference.type.desc),
							}"
							data-test="button"
						>
							{{ preference.type.desc }}
						</span>
						<span>
							{{
								alertStatus({
									type: preference.type.desc,
									optInInd: preference.optInInd,
								})
							}}
							<span v-if="disableSetting(preference.type.desc)">
								<router-link
									v-if="canUpdateCompanyProfile"
									:to="{ name: 'Company Profile' }"
									class="type"
									data-test="router-link"
									>Company Profile</router-link
								>
								<span v-else>Company Profile</span>
							</span>
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { sortBy } from 'lodash';

import AlertTypeIcon from '../AlertTypeIcon.vue';
import LoadingIcon from '../../../components/global/LoadingIcon.vue';
import QuickActionsMenu from '../QuickActionsMenu.vue';
import { VIEWS } from './constants.js';
import loansService from '@/services/contacts/loans-service.js';

export default {
	name: 'LoanSettings',
	components: { AlertTypeIcon, LoadingIcon, QuickActionsMenu },
	props: {
		loanId: Number,
	},
	data() {
		return {
			googleMyBusinessURL: '',
			settings: {},
			isLoading: false,
		};
	},
	computed: {
		...mapGetters(['googleMyBusinessURLGetter']),
		...mapState({
			socialMediaDetails: (state) => state.companyProfile.socialMediaDetails,
			roles: (state) => state.profile.roles,
		}),
		googleMyBusinessLink() {
			const gmb = this.socialMediaDetails.find(
				(socialMedia) => socialMedia.type === 'GoogleMyBusiness',
			);
			return gmb ? gmb.value : '';
		},
		canUpdateCompanyProfile() {
			return this.roles.some(
				(role) => role === 'BrokerOwner' || role === 'MarketingManager',
			);
		},
	},
	methods: {
		handleConfigureAlerts(alertPreference) {
			const { loanId, leadId } = this.settings;
			this.$emit('configureAlerts', {
				alertPreference,
				loanId,
				leadId,
				// eslint-disable-next-line no-undef
				view: VIEWS.ALERT_SETTINGS,
			});
		},
		disableSetting(type) {
			if (type === 'Review Me' && this.googleMyBusinessLink === '') {
				return true;
			}
			return false;
		},
		alertStatus({ type, optInInd }) {
			if (type === 'Review Me' && this.googleMyBusinessURL === '') {
				return 'Google My Business link required in';
				// optInInd value is null(Enabled) by default - Global setting
			} else if (optInInd === false) {
				return 'Disabled';
			} else {
				return 'Enabled';
				// deepcode ignore JavascriptDeadCode: This is wrong, why would a closing brace be unreachable code?
			}
		},
		sortedPreferences(preferences) {
			return sortBy(preferences, (preferences) => preferences.type.desc);
		},
	},
	async created() {
		this.isLoading = true;
		this.googleMyBusinessURL = this.googleMyBusinessURLGetter;
		const response = await loansService.getPreferencesByLoanId(this.loanId);
		const sortedPreferences = sortBy(
			response.preferences.filter((socMed) => socMed.type.desc !== 'Review Me'),
			(preference) => preference.type.desc,
		);
		this.settings = {
			...response,
			...{ preferences: this.sortedPreferences(response.preferences) },
		};
		this.isLoading = false;
	},
};
</script>

<style lang="scss" scoped>
.alert-container {
	cursor: pointer;
	display: flex;
	justify-content: space-between;
	align-items: center;
	@include rem('padding', 18px 20px);
	border-bottom: 1px solid $border-color-light;

	&:last-child {
		border-bottom: none;
	}
}

.alert-type-container {
	display: flex;
}

.alert-type {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	@include rem('margin-left', 15px);
}

.type {
	color: $brand-blue;
	font-weight: 500;
	cursor: pointer;
}

.disable {
	color: $b360-color--gray__medium-plus;
	pointer-events: none;
}

.type.disable {
	color: $b360-color--gray__medium-plus;
}
</style>
